import { produce } from 'immer';
import { groupBy, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Collapse, Link, Popconfirm, Table } from 'tdesign-react';
import { DeleteIcon } from 'tdesign-icons-react';
import VarInput, {
  convertDetailToProps,
} from '@/pages/Maintenance/Template/Variables/components/VarInput';
import { isVoid } from '@/utils/common';
import { getKeyNode } from '../..';

const { Panel } = Collapse;

export default function VariableTable({
  host,
  ipVariables,
  setIpVariables,
  doDelete,
  variables,
  descTitle,
}) {
  const [currentValue, setCurrentValue] = useState();

  const hostIpVariables = useMemo(() => {
    return ipVariables.filter((item) => item.hostId === host.id);
  }, [ipVariables, host]);

  const columns = [
    {
      colKey: 'key',
      title: '变量key',
      width: '20%',
      cell: ({ row }) => getKeyNode(variables, row),
    },
    {
      colKey: 'keyName',
      title: '变量名',
      width: '20%',
    },
    {
      colKey: 'defaultValue',
      title: '输入变量值',
      edit: {
        component: VarInput,
        validateTrigger: 'change',
        props: ({ row }) => ({
          inputProps: convertDetailToProps(row.showDetail),
          type: row.showType,
          clearable: true,
          style: { width: '100%' },
        }),
        on: () => ({
          onChange: onRowChange,
        }),
        rules: [{ required: true, message: '必填' }], // 所有变量都必填
      },
    },
    {
      colKey: 'desc',
      title: descTitle,
      ellipsis: true,
      width: '18%',
    },
    {
      colKey: 'option',
      title: '',
      fixed: 'right',
      width: 50,
      cell: ({ row }) =>
        row.canDelete ? (
          <Popconfirm
            theme='danger'
            content='删除所有相关IP变量？'
            onConfirm={() => doDelete(row.key)}
          >
            <Link hover='color' theme='danger' suffixIcon={<DeleteIcon />} />
          </Popconfirm>
        ) : null,
    },
  ];

  // 编辑
  const onRowChange = (params) => {
    const { editedRow, value } = params;
    const nextVariables = produce(ipVariables, (draft) => {
      draft.forEach((item) => {
        if (item.key === editedRow.key && item.hostId === editedRow.hostId) {
          item.defaultValue = value;
        }
      });
    });
    setIpVariables(nextVariables);
  };

  const grouped = useMemo(() => {
    return groupBy(hostIpVariables, 'groupId') || {};
  }, [hostIpVariables]);

  useEffect(() => {
    const fristGroup = Object.entries(grouped)[0] || [];
    const fristGroupId = fristGroup[0];
    setCurrentValue((current) => {
      // 只初始化
      if (isEmpty(current)) {
        return isVoid(fristGroupId) ? null : [fristGroupId];
      }
      return current;
    });
  }, [grouped]);

  return (
    !isEmpty(hostIpVariables) && (
      <Root>
        <SubTitle>{host.serviceIp || host.hostIp}</SubTitle>
        {!isEmpty(grouped) && (
          <Collapse value={currentValue} onChange={setCurrentValue} borderless>
            {Object.entries(grouped).map(([groupId, variables]) =>
              isEmpty(variables) ? null : (
                <Panel value={groupId} key={groupId} header={variables[0].groupName}>
                  <Table
                    columns={columns}
                    colKey='id'
                    maxHeight={400}
                    data={variables}
                    editableRowKeys={variables.map((item) => item.id)}
                  />
                </Panel>
              ),
            )}
          </Collapse>
        )}
      </Root>
    )
  );
}

const Root = styled.div`
  .t-table th {
    background-color: white !important;
  }
  .t-input-adornment {
    .t-input-adornment__prepend,
    .t-input-adornment__append {
      min-width: 0;
      height: 32px;
    }
    .t-input__wrap {
      flex: 1;
      min-width: 0;
    }
  }
  .t-collapse-panel__header {
    height: 40px;
    box-sizing: border-box;
    font-weight: normal;
    color: var(--td-text-color-secondary);
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
    background: #f7f8fa;
    margin-top: 8px;
  }
  .t-collapse-panel__content {
    padding: 0;
  }
`;

const SubTitle = styled.div`
  color: var(--td-text-color-primary);
  font-size: 14px;
  margin-top: 14px;
`;
